/**
 * The account-lifecycle gate for authenticated routes that live OUTSIDE the app shell.
 *
 * `ShellGate` folds this check into its single gate for everything inside the shell; this is the
 * component form for the handful of routes that render their own chrome (the deal console). It
 * exists so those routes are not the hole in an otherwise complete gate — a suspended account
 * reaching a negotiation console through a bookmarked URL is exactly the case a status gate is
 * for.
 *
 * Placed OUTSIDE `OnboardingGuard` where both are used: a rejected account with no organization
 * satisfies "needs onboarding" as well, and the wizard is the wrong answer for someone whose
 * application has already been refused.
 */
import { Navigate } from "react-router-dom";

import { GuardLoadingScreen } from "@/components/guards/RoleGuard";
import { useAccountStatus } from "@/hooks/useAccountStatus";

export function AccountAccessGuard({ children }: { children: React.ReactNode }) {
  const { access } = useAccountStatus();

  if (access === "loading") return <GuardLoadingScreen />;
  if (access === "blocked") return <Navigate to="/pending-approval" replace />;
  return <>{children}</>;
}

export default AccountAccessGuard;
